import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AccountService } from './profile-service';
import { useAuth } from './auth-context';

interface SupplierProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Protect routes so only users with user_type 'supplier' or 'both' can access
 */
export function SupplierProtectedRoute({
  children,
  redirectTo = '/login',
}: SupplierProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [isSupplier, setIsSupplier] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkSupplierAccess = async () => {
      if (!user) {
        setIsSupplier(false);
        setIsChecking(false);
        return;
      }

      try {
        const accountService = new AccountService();
        const account = await accountService.getAccount(user.id);
        
        if (!account) {
          setIsSupplier(false);
        } else {
          // User must have user_type 'supplier' or 'both' to access supplier features
          setIsSupplier(account.user_type === 'supplier' || account.user_type === 'both');
        }
      } catch (error) {
        console.error('Error checking supplier access:', error);
        setIsSupplier(false);
      } finally {
        setIsChecking(false);
      }
    };
    if (!isLoading) {
      checkSupplierAccess();
    }
  }, [user, isLoading]);

  if (isLoading || isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !isSupplier) {
    // If not supplier, redirect to dashboard selector if logged in, else login
    return <Navigate to={isAuthenticated ? '/dashboard' : redirectTo} replace />;
  }

  return <>{children}</>;
}
